import { Box, ButtonBase, Stack, Typography } from '@mui/material';
import React from 'react';
import { QuizWordExercise } from '../../mockLessonContent';
import { ERROR_TEXT, GREEN, INTER, NAVY, POPPINS, RED, SLATE, SUCCESS_TEXT, SURFACE_SOFT } from '../../studentStyles';
import AudioPlayButton from '../AudioPlayButton';
import ExerciseLabel from './ExerciseLabel';

interface QuizWordExerciseCardProps {
    exercise: QuizWordExercise;
    onReady: () => void;
}

// Deterministic ordering so the choices keep their places across re-renders. The
// offset is never 0, so the answer never sits first.
function orderChoices<T>(choices: T[], seed: string): T[] {
    const seedSum = seed.split('').reduce((sum, char) => sum + char.charCodeAt(0), 0);
    const offset = 1 + (seedSum % Math.max(1, choices.length - 1));
    return choices.map((_, index) => choices[(index + offset) % choices.length]);
}

// "Quiz: Character Recognition" — the teacher says the word, the student picks
// the characters out of a row of look-alikes. Pinyin stays hidden until answered.
export default function QuizWordExerciseCard({ exercise, onReady }: QuizWordExerciseCardProps) {
    const [wrongPicks, setWrongPicks] = React.useState<string[]>([]);
    const [solved, setSolved] = React.useState(false);

    const choices = React.useMemo(
        () => orderChoices([exercise.answer, ...exercise.distractors], exercise.id),
        [exercise.answer, exercise.distractors, exercise.id],
    );

    const handlePick = (hanzi: string) => {
        if (hanzi === exercise.answer.hanzi) {
            setSolved(true);
            onReady();
        } else {
            setWrongPicks(prev => (prev.includes(hanzi) ? prev : [...prev, hanzi]));
        }
    };

    return (
        <Box>
            <ExerciseLabel>CHARACTER CHECK</ExerciseLabel>
            <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 20, color: NAVY, lineHeight: 1.35 }}>
                {exercise.prompt}
            </Typography>
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                <AudioPlayButton ariaLabel={`Play audio for ${exercise.answer.hanzi}`} size={56} />
            </Box>
            <Stack direction="row" flexWrap="wrap" justifyContent="center" useFlexGap spacing={1.25} sx={{ mt: 3 }}>
                {choices.map(choice => {
                    const isWrong = wrongPicks.includes(choice.hanzi);
                    const isCorrect = solved && choice.hanzi === exercise.answer.hanzi;
                    return (
                        <ButtonBase
                            key={choice.hanzi}
                            onClick={() => handlePick(choice.hanzi)}
                            disabled={solved || isWrong}
                            sx={{
                                minWidth: 104, px: 2, py: 1.75, borderRadius: '18px',
                                flexDirection: 'column',
                                backgroundColor: isCorrect ? `${GREEN}14` : isWrong ? `${RED}14` : SURFACE_SOFT,
                                border: `2px solid ${isCorrect ? GREEN : isWrong ? RED : 'transparent'}`,
                                opacity: solved && !isCorrect ? 0.5 : 1,
                                transition: 'background-color 150ms ease, border-color 150ms ease, opacity 150ms ease',
                            }}
                        >
                            <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 30, color: NAVY, lineHeight: 1.2 }}>
                                {choice.hanzi}
                            </Typography>
                            {(solved || isWrong) && (
                                <Typography sx={{ fontFamily: INTER, fontSize: 12, color: SLATE, mt: 0.5 }}>
                                    {choice.pinyin}
                                </Typography>
                            )}
                        </ButtonBase>
                    );
                })}
            </Stack>
            {(solved || wrongPicks.length > 0) && (
                <Typography sx={{ fontFamily: INTER, fontWeight: 600, fontSize: 13, color: solved ? SUCCESS_TEXT : ERROR_TEXT, mt: 2, textAlign: 'center' }}>
                    {solved ? `对了! ${exercise.answer.hanzi} — ${exercise.answer.pinyin}` : 'Not that one — listen again and try another'}
                </Typography>
            )}
        </Box>
    );
}
